import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import Page from '../../components/Page';
import Breadcrumb from '../../components/Breadcrumb';
import client from '../../lib/feathers';

const STATUS_BADGE = {
  draft:     'bg-gray-100 text-gray-700',
  scheduled: 'bg-yellow-100 text-yellow-800',
  sending:   'bg-blue-100 text-blue-800',
  sent:      'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const TYPE_LABELS = {
  claim_listing:  'Claim Listing',
  access_pass:    'Access Pass',
  staff_access:   'Staff Access',
  upgrade:        'Upgrade',
  newsletter:     'Newsletter',
  custom:         'Custom',
};

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
        <dd className="mt-1 text-3xl font-semibold text-gray-900">{value}</dd>
        {sub && <dd className="mt-1 text-xs text-gray-400">{sub}</dd>}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const classes = STATUS_BADGE[status] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${classes}`}>
      {status}
    </span>
  );
}

export default function Outreach() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [totalContacts, setTotalContacts] = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [contactsResult, campaignsResult] = await Promise.all([
          client.service('admin/outreach-contact').find({ query: { $limit: 0 } }),
          client.service('admin/outreach-campaign').find({
            query: { $limit: 5, $sort: { createdAt: -1 } },
          }),
        ]);
        setTotalContacts(contactsResult.total);
        setCampaigns(campaignsResult.data || []);
      } catch (e) {
        console.error('Outreach dashboard load error', e);
        setError('Failed to load outreach data. Please refresh and try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Derive summary metrics from the recent campaigns list.
  // For a proper overall open rate we use only sent campaigns that have stats.
  const sentCampaigns = campaigns.filter((c) => c.status === 'sent' && c.stats);
  const totalSentMessages = sentCampaigns.reduce((sum, c) => sum + (c.stats?.sent || 0), 0);
  const totalOpenedMessages = sentCampaigns.reduce((sum, c) => sum + (c.stats?.opened || 0), 0);
  const overallOpenRate =
    totalSentMessages > 0
      ? `${((totalOpenedMessages / totalSentMessages) * 100).toFixed(1)}%`
      : '—';

  const campaignsSentCount = campaigns.filter((c) => c.status === 'sent').length;

  return (
    <Page title="Outreach">
      {/* Header */}
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[{ name: 'Outreach' }]} />
        </nav>
        <div className="mt-3 sm:mt-0 sm:ml-4 flex items-center space-x-3">
          <Link
            to="/admin/outreach/contacts"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Contacts
          </Link>
          <Link
            to="/admin/outreach/journeys"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Journeys
          </Link>
          <Link
            to="/admin/outreach/campaigns"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Campaigns
          </Link>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="mt-4 rounded-md bg-red-50 border border-red-200 px-4 py-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Summary stat cards */}
      <div className="mt-6">
        {loading ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white overflow-hidden shadow rounded-lg animate-pulse">
                <div className="px-4 py-5 sm:p-6">
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-3" />
                  <div className="h-8 bg-gray-200 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Total Contacts"
              value={totalContacts !== null ? totalContacts.toLocaleString() : '—'}
              sub="Active outreach contacts"
            />
            <StatCard
              label="Campaigns Sent"
              value={campaignsSentCount.toLocaleString()}
              sub="From recent campaigns"
            />
            <StatCard
              label="Overall Open Rate"
              value={overallOpenRate}
              sub="Across sent campaigns"
            />
            <StatCard
              label="Messages Delivered"
              value={totalSentMessages.toLocaleString()}
              sub="From recent campaigns"
            />
          </dl>
        )}
      </div>

      {/* Recent campaigns table */}
      <div className="mt-8">
        <div className="sm:flex sm:items-center sm:justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-900">Recent Campaigns</h2>
          <Link
            to="/admin/outreach/campaigns"
            className="text-sm font-medium text-primary hover:text-red-900"
          >
            View all campaigns
          </Link>
        </div>

        <div className="flex flex-col">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Campaign
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Type
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Sent
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Open Rate
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Date
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">View</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading && (
                      <tr>
                        <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-400">
                          Loading...
                        </td>
                      </tr>
                    )}
                    {!loading && campaigns.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-400">
                          No campaigns yet.{' '}
                          <Link
                            to="/admin/outreach/campaigns"
                            className="text-primary hover:text-red-900 font-medium"
                          >
                            Create your first campaign
                          </Link>
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      campaigns.map((campaign) => {
                        const sentCount = campaign.stats?.sent ?? null;
                        const openedCount = campaign.stats?.opened ?? 0;
                        const openRate =
                          sentCount > 0
                            ? `${((openedCount / sentCount) * 100).toFixed(1)}%`
                            : '—';
                        const dateValue = campaign.sentAt || campaign.createdAt;

                        return (
                          <tr key={campaign.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {campaign.name}
                              </div>
                              {campaign.subject && (
                                <div className="text-xs text-gray-400 truncate max-w-xs">
                                  {campaign.subject}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {TYPE_LABELS[campaign.type] || campaign.type}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <StatusBadge status={campaign.status} />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {sentCount !== null ? sentCount.toLocaleString() : '—'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {campaign.status === 'sent' ? openRate : '—'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {dateValue ? format(new Date(dateValue), 'MMM d, yyyy') : '—'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <Link
                                to={`/admin/outreach/campaigns/${campaign.id}`}
                                className="text-primary hover:text-red-900"
                              >
                                View
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick links */}
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Link
          to="/admin/outreach/contacts"
          className="relative group bg-white rounded-lg shadow px-6 py-5 flex items-center space-x-4 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          <div className="flex-shrink-0 bg-red-50 rounded-lg p-3 text-primary">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-5-4M9 20H4v-2a4 4 0 015-4m0 0a4 4 0 110-8 4 4 0 010 8zm8-8a4 4 0 110-8 4 4 0 010 8z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">Contacts</p>
            <p className="text-sm text-gray-500">Browse, search, and manage outreach contacts by listing</p>
          </div>
          <svg className="h-5 w-5 text-gray-300 group-hover:text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
          </svg>
        </Link>

        <Link
          to="/admin/outreach/campaigns"
          className="relative group bg-white rounded-lg shadow px-6 py-5 flex items-center space-x-4 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          <div className="flex-shrink-0 bg-red-50 rounded-lg p-3 text-primary">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">Campaigns</p>
            <p className="text-sm text-gray-500">Create, schedule, and send email campaigns to contacts</p>
          </div>
          <svg className="h-5 w-5 text-gray-300 group-hover:text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
          </svg>
        </Link>

        <Link
          to="/admin/outreach/journeys"
          className="relative group bg-white rounded-lg shadow px-6 py-5 flex items-center space-x-4 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          <div className="flex-shrink-0 bg-red-50 rounded-lg p-3 text-primary">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">Journeys</p>
            <p className="text-sm text-gray-500">Automated email sequences for onboarding and outreach</p>
          </div>
          <svg className="h-5 w-5 text-gray-300 group-hover:text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
          </svg>
        </Link>
      </div>
    </Page>
  );
}
